import {
  formatCVV,
  formatCreditCardNumber,
  passwordRegExp,
} from "@/global/utils/utils";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ChangeEvent, ReactNode, useState, useEffect } from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import { IosSwitch } from "../IosSwitch/IosSwitch";
import {
  InputError,
  InputLabel,
} from "@/shared/common/formHelper/form_components";

interface AptUnitProps {
  name?: string;
}

export const AptUnit = (props: AptUnitProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "aptUnit";

  const error = errors[fieldName]?.message as string;

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName)}
        fullWidth
        placeholder={"Apt Unit"}
        size="small"
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface ZipCodeProps {
  name?: string;
  placeholder?: string;
  options?: RegisterOptions;
}

export const ZipCode = (props: ZipCodeProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "zipCode";

  const error = errors[fieldName]?.message as string;
  const placeholder = props?.placeholder || "Zip Code";
  const options = props?.options || {};
  options.required = props?.options?.required || "Zip Code is Required";

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, options)}
        fullWidth
        placeholder={placeholder}
        size="small"
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface FisrtNameProps {
  name?: string;
  placeholder?: string;
  requiredText?: string;
  options?: RegisterOptions;
}

export const FirstName = (props: FisrtNameProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "firstName";

  const error = errors[fieldName]?.message as string;
  const placeholder = props?.placeholder || "First Name";
  const options = props?.options || {};
  options.required = props?.options?.required || "First Name is Required";

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, options)}
        fullWidth
        placeholder={placeholder}
        size="small"
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface LastNameProps {
  name?: string;
  placeholder?: string;
  options?: RegisterOptions;
}

export const LastName = (props: LastNameProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "lastName";

  const error = errors[fieldName]?.message as string;
  const placeholder = props?.placeholder || "Last Name";
  const options = props?.options || {};
  options.required = props?.options?.required || "Last Name is Required";

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, options)}
        fullWidth
        placeholder={placeholder}
        size="small"
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface PhoneNumberProps {
  name?: string;
}

export const PhoneNumber = (props: PhoneNumberProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "phoneNumber";

  const error = errors[fieldName]?.message as string;

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          required: "Phone Number is Required",
          minLength: {
            message: "Phone Number should have 10 digits",
            value: 10,
          },
          maxLength: {
            message: "Phone Number should have 10 digits",
            value: 10,
          },
        })}
        fullWidth
        placeholder={"Phone Number"}
        size="small"
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface CardNumberProps {
  name?: string;
  placeholder?: string;
  options?: Omit<RegisterOptions, "minLength" | "maxLength"> & {
    minLength?: {
      value?: number;
      message?: string;
    };
    maxLength?: {
      value?: number;
      message?: string;
    };
  };
}

export const CardNumber = (props: CardNumberProps) => {
  const {
    register,
    setValue,
    trigger,
    formState: { errors, submitCount },
  } = useFormContext();
  const fieldName = props.name || "cardNumber";
  const placeholder = props?.placeholder || "Card Number";

  const options = props?.options || {};
  options.required = props?.options?.required || "Card Number is Required";
  options.minLength = {
    value: props?.options?.minLength?.value || 17,
    message:
      props?.options?.minLength?.message ||
      "Please enter 15/16 digit card number.",
  };
  options.maxLength = {
    value: props?.options?.maxLength?.value || 20,
    message: "Please enter 15/16 digit card number.",
  };

  const error = errors[fieldName]?.message as string;

  const handleCardInput = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const formatted = formatCreditCardNumber(value);
    setValue("cardNumber", formatted);
    submitCount > 0 && trigger("cardNumber");
  };

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, options as RegisterOptions)}
        fullWidth
        onChange={handleCardInput}
        placeholder={placeholder}
        size="small"
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface CvvProps {
  name?: string;
  placeholder?: string;
  options?: Omit<RegisterOptions, "minLength" | "maxLength"> & {
    minLength?: {
      value?: number;
      message?: string;
    };
    maxLength?: {
      value?: number;
      message?: string;
    };
  };
}

export const Cvv = (props: CvvProps) => {
  const {
    register,
    setValue,
    trigger,
    formState: { errors, submitCount },
  } = useFormContext();
  const fieldName = props.name || "cvv";
  const placeholder = props?.placeholder || "CVV";

  const options = props?.options || {};
  options.required = props?.options?.required || "CVV is Required";
  options.minLength = {
    value: props?.options?.minLength?.value || 3,
    message: props?.options?.minLength?.message || "Please enter 3/4 digit cvv",
  };
  options.maxLength = {
    value: props?.options?.maxLength?.value || 4,
    message: props?.options?.maxLength?.message || "Please enter 3/4 digit cvv",
  };

  const error = errors[fieldName]?.message as string;

  const handleCardCVV = (event: ChangeEvent<HTMLInputElement>) => {
    const value = formatCVV(event.target.value);
    setValue(fieldName, value);
    submitCount > 0 && trigger("cvv");
  };

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, options as RegisterOptions)}
        fullWidth
        placeholder={placeholder}
        size="small"
        onChange={handleCardCVV}
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface SaveCardProps {
  name?: string;
}

export const SaveCard = (props: SaveCardProps) => {
  const {
    formState: { errors },
    control,
  } = useFormContext();
  const fieldName = props.name || "sendNow";
  const theme = useTheme();

  const error = errors[fieldName]?.message as string;

  return (
    <Stack pl={1}>
      <Stack direction="row" alignItems="center">
        <Controller
          control={control}
          name={fieldName}
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              control={<IosSwitch checked={value} onChange={onChange} />}
              label={
                <Typography variant="body2" color="text.secondary" pl={1}>
                  Save card for future purchase
                </Typography>
              }
            />
          )}
        />
      </Stack>
      {!!error && (
        <Typography color="error.main" ml={1} fontSize={12} minHeight={18}>
          {error}
        </Typography>
      )}
    </Stack>
  );

  // //TODO: Update the checkbox to the iosSwithc
  // return (
  //   <Stack direction={"row"} alignItems={"center"}>
  //     <Box sx={{ display: "flex", alignItems: "center" }}>
  //       <Checkbox {...register("saveCard")} sx={{ marginLeft: "-8px" }} />
  //     </Box>
  //     <Box>
  //       <Box>
  //         <Typography variant="body2" color="text.secondary">
  //           Save credit card for fast checkout
  //         </Typography>
  //       </Box>
  //       <Box>
  //         {error ? (
  //           <Typography
  //             variant="caption"
  //             color={theme.palette.error.main}
  //             lineHeight={1.1}
  //           >
  //             {error}
  //           </Typography>
  //         ) : null}
  //       </Box>
  //     </Box>
  //   </Stack>
  // );
};

interface CouponCodeProps {
  name?: string;
  placeholder?: string;
  required?: string;
}

export const CouponCode = (props: CouponCodeProps) => {
  const {
    register,
    getValues,
    formState: { errors },
    setValue,
  } = useFormContext();
  const fieldName = props.name || "couponCode";
  const placeholder = props?.placeholder || "Gift Card Code";

  const error = errors[fieldName]?.message as string;

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          required: props?.required || "Code is Required",
        })}
        fullWidth
        placeholder={placeholder}
        size="small"
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface EmailProps {
  name?: string;
}

export const Email = (props: EmailProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "email";

  const error = errors[fieldName]?.message as string;

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          required: "Email is Required",
        })}
        fullWidth
        type="email"
        placeholder={"Email"}
        size="small"
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface MessageProps {
  name?: string;
  placeholder?: string;
  required?: boolean;
}

export const Message = (props: MessageProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const fieldName = props.name || "message";
  const placeholder = props?.placeholder || "Message";

  const error = errors[fieldName]?.message as string;

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          required: props?.required ? "Message is Required" : undefined,
        })}
        fullWidth
        placeholder={placeholder}
        size="small"
        multiline
        rows={4}
      />
      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
};

interface SendNowProps {
  name?: string;
}

export const SendNow = (props: SendNowProps) => {
  const {
    formState: { errors },
    control,
  } = useFormContext();
  const fieldName = props.name || "sendNow";

  const error = errors[fieldName]?.message as string;

  return (
    <Stack>
      <Stack direction="row" alignItems="center">
        <Controller
          control={control}
          name={fieldName}
          defaultValue={true}
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              control={
                <Box ml="11px">
                  <IosSwitch checked={value} onChange={onChange} />
                </Box>
              }
              label={
                <Typography variant="body2" fontSize={"1.1rem"} pl={1}>
                  Send Now
                </Typography>
              }
            />
          )}
        />
      </Stack>
      {!!error && (
        <Typography color="error.main" ml={1} fontSize={12} minHeight={18}>
          {error}
        </Typography>
      )}
    </Stack>
  );
};

interface PasswordProps {
  fieldName?: string;
  placeholder?: string;
  type: "password" | "text";
  endAdornment: ReactNode;
  shouldValidate?: boolean;
}

export function Password({
  type,
  placeholder,
  endAdornment,
  fieldName = "password",
  shouldValidate = false,
}: PasswordProps) {
  const theme = useTheme();
  const {
    setValue,
    register,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const errorMessage = errors[fieldName]?.message as string;
  const [password, setPassword] = useState("");
  const [warningMessage, setWarningMessage] = useState("");

  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    setPassword(value);
    clearErrors(fieldName);
  };

  // registering to hook form context
  useEffect(() => {
    if (shouldValidate)
      register(fieldName, {
        required: "Password is required",
        pattern: {
          value: passwordRegExp(),
          message:
            "Password should have at least 8 characters, one special character, one digit, one lowercase letter, and one uppercase letter.",
        },
      });
    else register(fieldName);
  }, [shouldValidate, register]);

  // setting value to hook form context and showing warning message
  useEffect(() => {
    if (shouldValidate) {
      setValue(fieldName, password);
      let timeout = setTimeout(() => {
        if (!!password?.length && passwordRegExp().test(password) === false) {
          setWarningMessage(
            "Password should have at least 8 characters, one special character, one digit, one lowercase letter, and one uppercase letter."
          );
        } else {
          setWarningMessage("");
        }
        clearTimeout(timeout);
      }, 300);
    } else setValue(fieldName, password);
  }, [shouldValidate, password]);

  return (
    <Box width={"100%"}>
      <TextField
        name={fieldName}
        type={type}
        size="small"
        fullWidth
        placeholder={placeholder}
        onChange={handleChange}
        value={password}
        InputProps={{
          endAdornment,
          sx: {
            height: "40px",
            fontSize: "1rem",
          },
        }}
      />

      {shouldValidate ? (
        <Box
          p={warningMessage ? 1 : 0}
          mt={warningMessage ? 1 : 0}
          borderRadius={"4px"}
          width={"100%"}
          height={
            warningMessage
              ? theme.breakpoints.down("md")
                ? "unset"
                : "32px"
              : "0px"
          }
          sx={{
            transition: "all 0.2s ease-in-out",
            opacity: warningMessage ? 1 : 0,
          }}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"left"}
        >
          <Typography
            variant="body2"
            fontSize={"0.8rem"}
            color={theme.palette.error.main}
          >
            {warningMessage}
          </Typography>
        </Box>
      ) : null}

      {shouldValidate ? (
        <Box
          height={errorMessage ? "16px" : 0}
          width={"100%"}
          py={"2px"}
          pl={"2px"}
          sx={{ transition: "height 0.2s ease-in-out" }}
        >
          {errorMessage ? <InputError>{errorMessage}</InputError> : null}
        </Box>
      ) : null}
    </Box>
  );
}

export function PasswordToggleButton({
  onClick,
  open,
}: {
  onClick: () => void;
  open: boolean;
}) {
  return (
    <IconButton size="small" onClick={onClick} tabIndex={-1}>
      {!open ? <Visibility /> : <VisibilityOff />}
    </IconButton>
  );
}

interface ConfirmPasswordProps {
  fieldName?: string;
  placeholder?: string;
  type: "password" | "text";
  endAdornment: ReactNode;
}

export function ConfirmPassword({
  type,
  placeholder,
  endAdornment,
  fieldName = "confirmPassword",
}: ConfirmPasswordProps) {
  const theme = useTheme();
  const {
    watch,
    register,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const errorMessage = errors[fieldName]?.message as string;
  const [confirmPassword, setConfirmPassword] = useState("");
  const [warningMessage, setWarningMessage] = useState("");

  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    setConfirmPassword(value);
    clearErrors(fieldName);
  };

  const password = watch("password");

  // registering to hook form context
  useEffect(() => {
    register(fieldName, {
      required: "Password is required",
      validate: (confirmPwd: string) => {
        if (password != confirmPwd) {
          return "Password does not match";
        }
      },
    });
  }, [register, password]);

  // setting value to hook form context and showing warning message
  useEffect(() => {
    setValue(fieldName, confirmPassword);
    let timeout = setTimeout(() => {
      if (!!confirmPassword?.length && password != confirmPassword) {
        setWarningMessage("Password does not match.");
      } else {
        setWarningMessage("");
      }
      clearTimeout(timeout);
    }, 300);
  }, [confirmPassword, password]);

  return (
    <Box width={"100%"}>
      <TextField
        name={fieldName}
        type={type}
        size="small"
        fullWidth
        placeholder={placeholder}
        onChange={handleChange}
        value={confirmPassword}
        InputProps={{
          endAdornment,
          sx: {
            height: "40px",
            fontSize: "1rem",
          },
        }}
      />

      <Box
        p={warningMessage ? 1 : 0}
        mt={warningMessage ? 1 : 0}
        borderRadius={"4px"}
        width={"100%"}
        height={
          warningMessage
            ? theme.breakpoints.down("md")
              ? "unset"
              : "32px"
            : "0px"
        }
        sx={{
          transition: "all 0.2s ease-in-out",
          opacity: warningMessage ? 1 : 0,
        }}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"left"}
      >
        <Typography
          variant="body2"
          fontSize={"0.8rem"}
          color={theme.palette.error.main}
        >
          {warningMessage}
        </Typography>
      </Box>

      <Box
        height={errorMessage ? "16px" : 0}
        width={"100%"}
        py={"2px"}
        pl={"2px"}
        sx={{ transition: "height 0.2s ease-in-out" }}
      >
        {errorMessage ? <InputError>{errorMessage}</InputError> : null}
      </Box>
    </Box>
  );
}
